import { useEffect, useMemo, useRef, useState } from 'react';

import { useLazyQuery } from '@apollo/client/react';
import {
  Autocomplete,
  Chip,
  FormControl,
  FormHelperText,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Stack,
  TextField,
} from '@mui/material';

import type { UserSelectFragment } from '@graphql/__generated__/graphql';
import { RaLabel } from '@modules/_shared/components/form/RaLabel';
import { SEARCH_USERS } from '@modules/_shared/components/user-select/UserSelect';
import { UserAvatar } from '@modules/_shared/components/UserAvatar';
import { isEmailValid } from '@modules/_shared/utils/email-validation';
import { useLocale } from '@shared/hooks/locale';

import { freeRecipient, isFreeRecipient } from './recipientIds';
import type { EmailFormRecipient } from './types';

const userRecipient = (user: UserSelectFragment): EmailFormRecipient | null => {
  const email = user.primaryEmail[0]?.email;
  return email == null
    ? null
    : { id: user.id, email, name: user.full_name || undefined };
};

const recipientLabel = (recipient: EmailFormRecipient): string =>
  recipient.name ? `${recipient.name} <${recipient.email}>` : recipient.email;

// Splits pasted "a@x.com, b@y.com; c@z.com" into candidate addresses. A
// recipient copied out of a mail client comes as "John Doe <john@x.com>":
// the address between the angle brackets is the candidate.
const ADDRESS_IN_ANGLES = /<([^<>]+)>/;
const splitAddresses = (input: string): string[] =>
  input
    .split(/[,;\n]+/)
    .map(part => (ADDRESS_IN_ANGLES.exec(part)?.[1] ?? part).trim())
    .filter(Boolean);

const SEARCH_DEBOUNCE_MS = 250;

// The composer's To / Cc / Bcc field: CRM users from the search (chips with
// avatar and name) and bare addresses typed or pasted (chips with the
// address), both as `EmailFormRecipient`. A typed value becomes a chip on
// Enter, comma, semicolon, space or blur once it is a valid address; an
// invalid one stays in the input with an error under the field.
export const RecipientsField = ({
  label,
  value,
  onChange,
  required = false,
  autoFocus = false,
  // Bare addresses are chips too, unless the host wants CRM users only (a
  // campaign files each mail on its buyer lead: an address without one would
  // send and leave no trace of the recipient).
  allowFreeAddresses = true,
  labelAction,
}: {
  label: string;
  value: EmailFormRecipient[];
  onChange: (recipients: EmailFormRecipient[]) => void;
  required?: boolean;
  autoFocus?: boolean;
  allowFreeAddresses?: boolean;
  // On the label's line, so the field keeps the form's full width.
  labelAction?: React.ReactNode;
}) => {
  const { t } = useLocale();
  const [inputValue, setInputValue] = useState('');
  const [inputError, setInputError] = useState<
    'invalid' | 'contactsOnly' | null
  >(null);
  const [options, setOptions] = useState<UserSelectFragment[]>([]);
  const [searchUsers, { loading }] = useLazyQuery(SEARCH_USERS);
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // A response for an earlier query must not replace the options of the
  // latest one (the debounce clears the timer, not a request in flight).
  const searchSeq = useRef(0);
  // A refused paste: the browser inserts the text and fires `input`, which
  // would clear the error just set. Kept for that one event.
  const keepErrorOnNextInput = useRef(false);

  useEffect(() => {
    if (searchTimer.current != null) {
      clearTimeout(searchTimer.current);
    }
    const query = inputValue.trim();
    if (query.length < 2) {
      setOptions([]);
      return;
    }
    searchTimer.current = setTimeout(() => {
      searchSeq.current += 1;
      const seq = searchSeq.current;
      searchUsers({ variables: { q: query, limit: 8 } })
        .then(response => {
          if (seq === searchSeq.current) {
            setOptions(response.data?.search_users_tsv ?? []);
          }
        })
        .catch(searchError => {
          // The field keeps working for bare addresses; the search failure is
          // not the user's problem but must not vanish either.
          console.error('[RecipientsField] user search failed', searchError);
          if (seq === searchSeq.current) {
            setOptions([]);
          }
        });
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (searchTimer.current != null) {
        clearTimeout(searchTimer.current);
      }
    };
  }, [inputValue, searchUsers]);

  const selectedIds = useMemo(
    () => new Set(value.map(recipient => recipient.id)),
    [value],
  );
  const selectedEmails = useMemo(
    () => new Set(value.map(recipient => recipient.email.toLowerCase())),
    [value],
  );

  // Users already picked (by id) or already present as a bare address (same
  // mailbox) are not offered again.
  const visibleOptions = useMemo(
    () =>
      options.filter(user => {
        const email = user.primaryEmail[0]?.email?.toLowerCase();
        return (
          !selectedIds.has(user.id) &&
          email != null &&
          !selectedEmails.has(email)
        );
      }),
    [options, selectedIds, selectedEmails],
  );

  const addFromInput = (raw: string): boolean => {
    const candidates = splitAddresses(raw);
    if (candidates.length === 0) {
      return true;
    }
    if (!allowFreeAddresses) {
      // The text stays in the field with the reason under it: an address
      // that silently vanished reads as accepted and scrolled away.
      setInputError('contactsOnly');
      return false;
    }
    if (!candidates.every(isEmailValid)) {
      setInputError('invalid');
      return false;
    }
    const added = candidates
      .filter(email => !selectedEmails.has(email.toLowerCase()))
      .map(email => freeRecipient(email))
      // The same address twice in one paste is one chip.
      .filter(
        (recipient, index, all) =>
          all.findIndex(other => other.id === recipient.id) === index,
      );
    if (added.length > 0) {
      onChange([...value, ...added]);
    }
    keepErrorOnNextInput.current = false;
    setInputError(null);
    setInputValue('');
    return true;
  };

  const helperText =
    inputError === 'invalid'
      ? t('Invalid email')
      : inputError === 'contactsOnly'
      ? t('Only CRM contacts can receive a mass mail')
      : undefined;

  return (
    <FormControl fullWidth error={helperText != null}>
      {labelAction == null ? (
        <RaLabel label={label} required={required} />
      ) : (
        <Stack
          direction="row"
          sx={{
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 0.75,
          }}
        >
          <RaLabel label={label} required={required} sx={{ mb: 0 }} />
          {labelAction}
        </Stack>
      )}
      <Autocomplete<EmailFormRecipient | UserSelectFragment, true, false, true>
        multiple
        // Always free-text at the Autocomplete level (the generic wants a
        // literal); `addFromInput` is what refuses a bare address when the
        // host does not allow one.
        freeSolo
        options={visibleOptions}
        value={value}
        inputValue={inputValue}
        loading={loading}
        filterOptions={candidates => candidates}
        // A chip is a recipient; an option is a user: same key space (uuid)
        // once a user is picked, so equality is by id.
        isOptionEqualToValue={(option, selected) =>
          typeof option !== 'string' &&
          typeof selected !== 'string' &&
          option.id === selected.id
        }
        getOptionLabel={option =>
          typeof option === 'string'
            ? option
            : 'email' in option
            ? recipientLabel(option)
            : option.full_name ?? ''
        }
        onInputChange={(_, next, reason) => {
          if (reason === 'reset') {
            return;
          }
          if (keepErrorOnNextInput.current) {
            keepErrorOnNextInput.current = false;
          } else {
            setInputError(null);
          }
          // Comma or semicolon commits the address, as in every mail client.
          if (/[,;]$/.test(next) && addFromInput(next.slice(0, -1))) {
            return;
          }
          // So does a space after a complete address; a space inside a name
          // being searched ("John Do") commits nothing.
          if (allowFreeAddresses && /\s$/.test(next)) {
            const token = next.trim();
            if (
              token !== '' &&
              !/\s/.test(token) &&
              isEmailValid(token) &&
              addFromInput(token)
            ) {
              return;
            }
          }
          setInputValue(next);
        }}
        onChange={(_, next, reason) => {
          if (reason === 'createOption') {
            // Enter on free text: the strings in `next` are the typed value.
            const typed = next.filter(
              (entry): entry is string => typeof entry === 'string',
            );
            typed.forEach(entry => addFromInput(entry));
            return;
          }
          const recipients = next.flatMap(entry => {
            if (typeof entry === 'string') {
              return [];
            }
            if ('email' in entry) {
              return [entry];
            }
            const recipient = userRecipient(entry);
            return recipient != null ? [recipient] : [];
          });
          setInputError(null);
          // A pick from the list leaves the typed query in the (controlled)
          // input, which the next blur would flag as an invalid address.
          setInputValue('');
          onChange(recipients);
        }}
        onBlur={() => {
          addFromInput(inputValue);
        }}
        renderOption={(props, option) =>
          typeof option === 'string' || 'email' in option ? null : (
            <ListItem {...props} key={option.id} sx={{ py: 0.75 }}>
              <ListItemAvatar sx={{ minWidth: 40 }}>
                <UserAvatar user={option} size={32} />
              </ListItemAvatar>
              <ListItemText
                primary={option.full_name}
                secondary={option.primaryEmail[0]?.email}
                slotProps={{
                  primary: { sx: { fontSize: '0.875rem' } },
                  secondary: { sx: { fontSize: '0.75rem' } },
                }}
              />
            </ListItem>
          )
        }
        renderValue={(selected, getItemProps) =>
          selected.map((entry, index) => {
            if (typeof entry === 'string' || !('email' in entry)) {
              return null;
            }
            const { key, ...itemProps } = getItemProps({ index });
            return (
              <Chip
                key={key}
                size="small"
                label={recipientLabel(entry)}
                title={entry.email}
                variant={isFreeRecipient(entry) ? 'outlined' : 'filled'}
                {...itemProps}
              />
            );
          })
        }
        renderInput={params => (
          <TextField
            {...params}
            // Outlined on paper like the composer's other fields (RaTextField).
            variant="outlined"
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                background: 'var(--mui-palette-background-paper)',
              },
            }}
            autoFocus={autoFocus}
            placeholder={value.length === 0 ? t('Search users') : undefined}
            error={helperText != null}
            // A pasted recipient list is committed straight away: left in
            // the input it would concatenate with what is typed next, and
            // only the angle-bracketed address of the merged token would
            // survive splitAddresses. A paste that cannot be parsed lands in
            // the input with the error under it, to be corrected, not lost.
            onPaste={event => {
              // Spliced at the caret, over the selection: a paste in the
              // middle of an address completes it, a paste over a selected
              // one replaces it.
              const input = event.target as HTMLInputElement;
              const start = input.selectionStart ?? inputValue.length;
              const end = input.selectionEnd ?? inputValue.length;
              const text =
                inputValue.slice(0, start) +
                event.clipboardData.getData('text') +
                inputValue.slice(end);
              if (text.trim() === '') {
                return;
              }
              if (addFromInput(text)) {
                event.preventDefault();
              } else {
                keepErrorOnNextInput.current = true;
              }
            }}
          />
        )}
      />
      {helperText != null && <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  );
};
